/**
 * /applications — Application Tracker board.
 * Every job with a logged outcome, grouped by status, with follow-up
 * reminders when a thread has gone quiet.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import OutcomeWidget, { STATUS_META } from "../components/OutcomeWidget";
import ResmoMascot from "../components/ResmoMascot";
import { api } from "../lib/api";
import { ExternalLink, Loader, AlarmClock, ClipboardList, ArrowRight, Send } from "lucide-react";

const SECTION_ORDER = ["interview_request", "offer", "hired", "applied", "no_response", "rejection"];

const fitColor = (s) => (s >= 75 ? "#10B981" : s >= 60 ? "#FFC145" : "#DC2626");

function AppRow({ row, onChanged }) {
  return (
    <div
      className="flex flex-wrap items-center gap-3 px-4 py-3"
      style={{ background: "white", border: "2px solid #0F1B2D", borderRadius: 8, boxShadow: "3px 3px 0 0 #0F1B2D" }}
      data-testid={`app-row-${row.analysis_id}`}
    >
      <div className="flex-1 min-w-[220px]">
        <Link
          to={`/dashboard/${row.analysis_id}`}
          className="font-display text-base hover:underline"
          style={{ color: "#0F1B2D" }}
          data-testid={`app-row-title-${row.analysis_id}`}
        >
          {row.job_title || "Untitled role"}
        </Link>
        {row.company_name && (
          <span className="font-mono-rm text-xs ml-2 uppercase tracking-wider" style={{ color: "#475569" }}>
            @ {row.company_name}
          </span>
        )}
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {typeof row.fit_score === "number" && (
            <span className="font-mono-rm text-[10px] px-1.5 py-0.5 rounded text-white"
                  style={{ background: fitColor(row.fit_score) }}
                  data-testid={`app-row-fit-${row.analysis_id}`}>
              fit {row.fit_score}/100
            </span>
          )}
          <span className="text-[11px]" style={{ color: "#64748B" }}>
            {row.days_since_update === 0 ? "updated today" : `${row.days_since_update}d since last update`}
          </span>
          {row.follow_up && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded"
                  style={{ background: "#FEF3C7", color: "#B45309", border: "1px solid #B45309" }}
                  data-testid={`app-row-followup-${row.analysis_id}`}>
              <AlarmClock size={11} /> Time to follow up — {row.days_since_update}d quiet
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <OutcomeWidget analysisId={row.analysis_id} compact onChanged={onChanged} />
        {row.job_url && (
          <a href={row.job_url} target="_blank" rel="noopener noreferrer"
             className="rm-btn text-[11px] px-2.5 py-1"
             style={{ background: "#B45309", color: "white" }}
             title="Open the original job posting"
             data-testid={`app-row-joblink-${row.analysis_id}`}>
            <Send size={12} /> Posting
          </a>
        )}
        <Link to={`/dashboard/${row.analysis_id}`}
              className="rm-btn text-[11px] px-2.5 py-1"
              style={{ background: "#0F1B2D", color: "white" }}
              data-testid={`app-row-report-${row.analysis_id}`}>
          Report <ExternalLink size={12} />
        </Link>
      </div>
    </div>
  );
}

export default function Applications() {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/outcomes/board");
      setBoard(data);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't load your applications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rows = board?.rows || [];
  const counts = board?.counts || {};
  const followUps = rows.filter((r) => r.follow_up).length;

  return (
    <div className="min-h-screen rm-paper pb-20" data-testid="applications-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-10">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-mono-rm uppercase text-[10px] tracking-widest rm-text-orange mb-2">
              · Application Tracker
            </div>
            <h1 className="font-display text-3xl md:text-5xl uppercase leading-none" style={{ color: "#0F1B2D" }}>
              Every application, one board.
            </h1>
            <p className="text-sm mt-3 max-w-xl" style={{ color: "#475569" }}>
              Track where each tailored resume landed — update statuses as you hear back,
              and we'll flag the threads that need a follow-up.
            </p>
          </div>
          <ResmoMascot state="satisfied" size={120} flavor="resume" />
        </div>

        {/* Stat chips */}
        {rows.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="applications-stats">
            <span className="rm-tag" style={{ background: "#0F1B2D", color: "white" }} data-testid="applications-stat-total">
              {counts.total || 0} tracked
            </span>
            {SECTION_ORDER.map((s) =>
              counts[s] ? (
                <span key={s} className="rm-tag" style={{ background: STATUS_META[s]?.soft, color: STATUS_META[s]?.color }}
                      data-testid={`applications-stat-${s}`}>
                  {counts[s]} {STATUS_META[s]?.label}
                </span>
              ) : null,
            )}
            {followUps > 0 && (
              <span className="rm-tag inline-flex items-center gap-1"
                    style={{ background: "#FEF3C7", color: "#B45309" }}
                    data-testid="applications-stat-followups">
                <AlarmClock size={12} /> {followUps} need a follow-up
              </span>
            )}
          </div>
        )}

        {loading && (
          <div className="py-16 text-center" data-testid="applications-loading">
            <Loader className="animate-spin mx-auto" size={28} style={{ color: "#FF6A00" }} />
          </div>
        )}
        {error && !loading && (
          <div className="text-sm font-semibold" style={{ color: "#DC2626" }} data-testid="applications-error">{error}</div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div className="text-center py-14 px-6"
               style={{ background: "white", border: "3px solid #0F1B2D", borderRadius: 12, boxShadow: "4px 4px 0 0 #FF6A00" }}
               data-testid="applications-empty">
            <ClipboardList size={36} className="mx-auto mb-3" style={{ color: "#FF6A00" }} />
            <div className="font-display text-2xl mb-2" style={{ color: "#0F1B2D" }}>No applications tracked yet.</div>
            <p className="text-sm max-w-md mx-auto mb-6" style={{ color: "#475569" }}>
              When you hit "Apply on employer site" from a fit report or the Studio, the job lands here
              automatically. You can also log an outcome from any report.
            </p>
            <div className="flex justify-center gap-3 flex-wrap">
              <Link to="/job-search" className="rm-btn rm-btn-primary text-xs" data-testid="applications-empty-jobs-btn">
                Hunt live jobs <ArrowRight size={14} />
              </Link>
              <Link to="/history" className="rm-btn rm-btn-secondary text-xs" data-testid="applications-empty-history-btn">
                Browse my reports
              </Link>
            </div>
          </div>
        )}

        {!loading && SECTION_ORDER.map((status) => {
          const group = rows.filter((r) => r.status === status);
          if (!group.length) return null;
          const meta = STATUS_META[status] || {};
          return (
            <div key={status} className="mb-8" data-testid={`applications-section-${status}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="font-display text-lg uppercase tracking-wide" style={{ color: meta.color || "#0F1B2D" }}>
                  {meta.label || status}
                </span>
                <span className="font-mono-rm text-[10px] px-1.5 py-0.5 rounded"
                      style={{ background: meta.soft || "#F1F5F9", color: meta.color || "#334155" }}>
                  {group.length}
                </span>
              </div>
              <div className="space-y-3">
                {group.map((row) => <AppRow key={row.id} row={row} onChanged={load} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
